export const websiteBuilder = {
  seo: {
    title: "Website — Bespire AI assistant",
    description:
      "A bookable, mobile-ready website built and maintained by the Bespire AI. Not a separate product — one capability of your daily assistant.",
  },
  hero: {
    badge: "Website",
    title: "A website you build — and edit — by chatting",
    subtitle:
      "No template picker, no 3-week project. Tell it what you do, and the Bespire AI ships a live site: booking, services, pricing, SEO and mobile view. Change anything with one sentence.",
    ctaTrial: "Try it free for 3 days",
    ctaPricing: "See pricing →",
    sitePreview: {
      label: "Website preview · illustration",
      urlBar: "bella-studio.bespire.io",
      businessName: "Bella Studio",
      tagline: "Prenatal yoga that actually calms you down.",
      navItems: ["Classes", "About", "Booking", "Contact"],
      heroCta: "Book a class",
      infoCards: [
        { label: "Next class", value: "Thu 6:00 pm" },
        { label: "Spots left", value: "3 / 8" },
      ],
      trustLine: "New: A/B tested copy · SEO-ready · Mobile-friendly",
    },
  },
  process: {
    title: "Three steps to a live site",
    subtitle:
      "Same assistant that helps everywhere else — now also builds and maintains your website.",
    steps: [
      {
        step: "01",
        icon: "MessageCircle",
        title: "Tell it what you do",
        description:
          "One chat. No template picker, no designer brief — the Bespire AI asks the right questions and works from your answers.",
        bullets: [
          "5–10 minute short chat",
          "Can pull content from your existing site",
          "Uses your brand card automatically",
        ],
      },
      {
        step: "02",
        icon: "Wand2",
        title: "Get a live site",
        description:
          "Not a wireframe — a real site with booking, copy, SEO basics and a mobile view. Ready to go live today.",
        bullets: [
          "Booking, services, pricing, FAQ, contact",
          "SEO basics, meta tags, sitemap, canonical",
          "Mobile and tablet view out of the box",
        ],
      },
      {
        step: "03",
        icon: "Sparkles",
        title: "Edit with one sentence",
        description:
          "\"Make it warmer.\" \"Swap the image.\" \"Highlight the weekend package.\" The Bespire AI does it, you approve.",
        bullets: [
          "No more developers, tickets, waiting",
          "Versions: revert anything anytime",
          "Custom domain or free subdomain",
        ],
      },
    ],
  },
  beforeAfter: {
    title: "Before / after: same business",
    subtitle:
      "Not just a prettier site. A site plugged into your daily work.",
    before: {
      label: "Before",
      businessName: "Template website",
      tagline: "Shipped 3 months ago, haven't touched it since.",
      bio: "A developer put it together half a year ago. Booking on a separate tool. Every change means a ticket, waiting, extra cost. Nobody maintains the SEO.",
      problems: [
        "Booking on a separate system — 2 extra clicks",
        "Every edit needs a developer",
        "SEO and copy never get updated",
        "No data on what works and what doesn't",
      ],
    },
    after: {
      label: "After",
      businessName: "Customer site from Bespire",
      tagline: "One system: site, booking, content, measurement.",
      bio: "Website, booking, pricing and copy live in one place. The Bespire AI sees what people view and book — and suggests what to change.",
      wins: [
        "Booking on the site itself, one click",
        "Rewrite anything by chat, live same day",
        "SEO basics kept in shape automatically",
        "Data-driven suggestions — what to highlight",
      ],
    },
  },
  showcase: {
    title: "One prompt, several styles — you pick",
    subtitle:
      "Same business, different directions. The Bespire AI generates full-site variants live, not just palette samples.",
    styles: [
      {
        name: "Calm studio",
        heading: "Playfair",
        body: "Manrope",
        tone: "Warm · Quiet · Premium",
        palette: ["#F5B5C6", "#B8CBB5", "#EFE3D0", "#2B2530"],
        pages: "Home · Classes · Packages · About · Booking",
      },
      {
        name: "Modern clinic",
        heading: "Inter",
        body: "Inter",
        tone: "Clean · Expert · Trustworthy",
        palette: ["#0EA5E9", "#F1F5F9", "#0F172A", "#22D3EE"],
        pages: "Home · Services · Team · Booking · Blog",
      },
      {
        name: "Energetic fitness",
        heading: "Space Grotesk",
        body: "Manrope",
        tone: "Bold · Direct · Motivating",
        palette: ["#F97316", "#111827", "#F3F4F6", "#FDBA74"],
        pages: "Home · Programs · Coaches · Passes · Booking",
      },
      {
        name: "Natural / organic",
        heading: "Fraunces",
        body: "Nunito",
        tone: "Honest · Warm · Handmade",
        palette: ["#65A30D", "#F5F5DC", "#3F3F46", "#FDE68A"],
        pages: "Home · Treatments · About · Reviews · Booking",
      },
      {
        name: "Creative studio",
        heading: "Recoleta",
        body: "Manrope",
        tone: "Playful · Bold · Unique",
        palette: ["#8B5CF6", "#F472B6", "#FEF3C7", "#1F2937"],
        pages: "Home · Workshops · Portfolio · About · Booking",
      },
      {
        name: "Elegant coaching",
        heading: "Cormorant",
        body: "Inter",
        tone: "Quiet · Refined · Trust",
        palette: ["#334155", "#E2E8F0", "#C7A17A", "#0F172A"],
        pages: "Home · Programs · Method · Case studies · Booking",
      },
    ],
    included: {
      title: "In every variant",
      items: [
        "Booking system on the site",
        "Mobile and tablet view",
        "SEO basics, meta tags, sitemap",
        "GDPR-compliant, EU servers",
        "Custom domain or free subdomain",
        "Chat-based editing with versions",
      ],
    },
  },
  chat: {
    title: "Ask for a change like you'd ask a web person",
    subtitle:
      "Except no ticket, no next-week ETA — it's part of your subscription and it happens now.",
    chat: {
      title: "Website assistant",
      online: "Online",
      messages: [
        { role: "user" as const, content: "My site feels too cold. I want a warmer tone and a bigger booking button." },
        { role: "assistant" as const, content: "Got it. Here's what I'll change:", list: [
          "🎨 Warmer tones across the home page",
          "✍️ Friendlier copy, \"you\" address",
          "📅 CTA: bigger, sticky booking button",
        ] },
        { role: "user" as const, content: "Good. Also add a weekend package section." },
        { role: "assistant" as const, content: "Done: a new section under the hero, 3 weekend packages with image, price, booking button. Preview? 🚀" },
        { role: "user" as const, content: "Preview." },
        { role: "assistant" as const, content: "Sent you a preview link. Approve it and I'll ship it in under a minute." },
      ],
    },
  },
  liveExamples: {
    eyebrow: "Live examples",
    title: "See real customer sites",
    subtitle: "All built in Bespire in a few hours — click to see them live.",
    ctaLabel: "Open site →",
    items: [
      { name: "Bespire landing", tag: "SaaS", href: "/landing4" },
      { name: "TSMT partner page", tag: "Health", href: "/tsmt-partner" },
      { name: "Contractor service", tag: "Construction", href: "/contractors" },
    ],
  },
  crossLinks: {
    title: "A website alone isn't enough",
    subtitle: "The Bespire AI carries the same brand and content into booking, marketing, customer relations and your daily rhythm.",
  },
  cta: {
    title: "Build your site today",
    subtitle: "3-day free trial, no card required.",
    primary: "Try it free for 3 days",
    secondary: "See pricing",
  },
};
